import { service as serviceQuery, services as servicesQuery } from '@data/queries';
import { Container, Divider, Grid, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import PostBody from '@utils/PostBody';
import sanity from '@utils/sanity';
import { ReactElement } from 'react';
import { ServiceProps } from 'src/@types/service';
import MissingService from 'src/sections/services/MissingService';
import ServiceHero from 'src/sections/services/ServiceHero';
import ServiceInfo from 'src/sections/services/ServiceInfo';
import { Page } from '../../src/components';
import { HEADER_DESKTOP_HEIGHT, HEADER_MOBILE_HEIGHT } from '../../src/config';
import Layout from '../../src/layouts';
import { Advertisement01 } from '../../src/sections/advertisement';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  paddingTop: HEADER_MOBILE_HEIGHT,
  [theme.breakpoints.up('md')]: {
    paddingTop: HEADER_DESKTOP_HEIGHT,
  },
}));

// ----------------------------------------------------------------------

type Props = {
  service: ServiceProps;
  services: ServiceProps[];
};

export default function ServicePage({ service }: Props) {
  return (
    <Page
      seo={service.seo}
      canonical={'https://www.b-e-a.no/elektriker-tjenester/' + service.slug.current}
    >
      <RootStyle>
        <ServiceHero service={service} />

        <Container
          sx={{
            pt: { xs: 10, md: 10 },
            pb: { xs: 15, md: 10 },
          }}
        >
          <Grid container spacing={8}>
            <Grid item xs={12} md={7} lg={8}>
              <section>
                <PostBody content={service.content} />
              </section>
            </Grid>

            <Grid item xs={12} md={5} lg={4}>
              <Stack spacing={5}>
                <ServiceInfo service={service} />
                <Advertisement01
                  advertisement={{
                    title: 'Eeasee Elbillader',
                    cta: 'Se mer',
                    description: 'Verdens smarteste elbillader til kun 13.985,- ferdig montert',
                    imageUrl: 'image-a0be3eac397b7d6809ea422bbcdd51a058a93276-1440x960-jpg',
                    path: '/kampanjer/elbillader/',
                  }}
                />
              </Stack>
            </Grid>
          </Grid>
        </Container>

        <Divider
          sx={{
            display: { xs: 'none', md: 'block' },
          }}
        />

        <MissingService />
      </RootStyle>
    </Page>
  );
}

// ----------------------------------------------------------------------

ServicePage.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};

export const getStaticPaths = async () => {
  const services = await sanity.fetch(servicesQuery);
  const paths = services.map((service: ServiceProps) => ({
    params: { slug: service.slug.current },
  }));

  // We'll pre-render only these paths at build time.
  // { fallback: false } means other routes should 404.
  return { paths, fallback: false };
};

// This function gets called at build time on server-side.
export const getStaticProps = async ({ params }: any) => {
  const service = await sanity.fetch(serviceQuery, { slug: params.slug });
  const services = await sanity.fetch(servicesQuery);
  return { props: { service, services } };
};
